import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";
import { useBloqueioPersistencia } from "@/hooks/useAvisosTitulo";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium cursor-pointer transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 disabled:cursor-not-allowed [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground shadow hover:bg-primary/90",
        destructive: "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
        outline:
          "border border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground",
        secondary: "bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-9 px-4 py-2",
        sm: "h-8 rounded-md px-3 text-xs",
        lg: "h-10 rounded-md px-8",
        icon: "h-9 w-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  /** Força o botão a ser tratado como operação que grava no banco. */
  persiste?: boolean;
  /** Isenta o botão do bloqueio automático de gravação. */
  semBloqueio?: boolean;
}

/** Rótulos que indicam operação de gravação no banco. */
const RE_PERSISTE =
  /(salvar|gravar|excluir|apagar|remover|registrar|confirmar|importar|enviar|adicionar|incluir|atualizar|aplicar|lan[çc]ar|repor|transferir|duplicar|cadastrar|criar)/i;

function textoDe(node: React.ReactNode): string {
  if (node == null || typeof node === "boolean") return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(textoDe).join(" ");
  if (React.isValidElement(node))
    return textoDe((node.props as { children?: React.ReactNode }).children);
  return "";
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, persiste, semBloqueio, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    const { bloqueado, motivo } = useBloqueioPersistencia();
    const ehPersistencia =
      persiste ?? (props.type === "submit" || RE_PERSISTE.test(textoDe(props.children)));
    const bloquear = bloqueado && !semBloqueio && !asChild && ehPersistencia;
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
        disabled={bloquear || props.disabled}
        title={bloquear ? motivo : props.title}
      />
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
